import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { fetchPublicGrassland, fetchRegions } from "@/lib/api";
import { Fire, Warning, CheckCircle, Horse } from "@phosphor-icons/react";

/**
 * Öffentliches, iframe-einbettbares Fruktan-Widget für Pferdehalter.
 *
 * Route: /widget/fructan/:regionId
 *
 * - Kein Login, kein Sidebar, keine Navigation.
 * - Minimalistisches Design, optimiert für 300-480 px Breite.
 * - Kopierbarer Embed-Code wird im Grassland-Cockpit bereitgestellt.
 */
const LEVEL_META = {
  0: { label: "Niedrig",   color: "#00FA9A", bg: "rgba(0,250,154,0.12)", Icon: CheckCircle,
       headline: "Weide unbedenklich",
       body: "Aktuell kein erhöhtes Fruktan-Risiko — normaler Weidegang möglich." },
  1: { label: "Mäßig",    color: "#6B5C2D", bg: "#16140D", Icon: Warning,
       headline: "Leicht erhöhtes Risiko",
       body: "Empfindliche Pferde beobachten. Weidegang eher in Morgenstunden." },
  2: { label: "Erhöht",   color: "#FFC24B", bg: "rgba(255,215,0,0.10)", Icon: Warning,
       headline: "Fruktan-Risiko erhöht",
       body: "Weidezeit deutlich begrenzen (max. 1–2 Std. vormittags). Heu zufüttern." },
  3: { label: "Kritisch", color: "#FF5500", bg: "rgba(255,85,0,0.12)", Icon: Fire,
       headline: "Hufrehe-Alarm",
       body: "Rehe-gefährdete Pferde heute nicht auf die Weide. Heu rationieren." },
};

export default function FructanWidget() {
  const { regionId } = useParams();
  const [data, setData] = useState(null);
  const [regions, setRegions] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!regionId) return;
    fetchPublicGrassland(regionId)
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || "Region nicht verfügbar"));
    fetchRegions().then(setRegions).catch(() => {});
  }, [regionId]);

  if (error) {
    return (
      <div className="p-6 min-h-screen flex items-center justify-center bg-[#121212] text-sm text-zinc-400" data-testid="fructan-widget-error">
        <div className="max-w-sm text-center">
          <Warning size={28} className="mx-auto text-[#FFC24B]" />
          <p className="mt-2">{error}</p>
          <a href="https://bodenblick.com" target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-xs font-mono underline text-[#00FA9A]">
            bodenblick.com
          </a>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 min-h-screen flex items-center justify-center bg-[#121212] text-xs text-zinc-500" data-testid="fructan-widget-loading">
        Lade…
      </div>
    );
  }

  const score = data.fructan_risk?.score ?? 0;
  const meta = LEVEL_META[score];
  const Icon = meta.Icon;
  const updated = data.fetched_at ? new Date(data.fetched_at).toLocaleString("de-AT", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "–";

  return (
    <div className="min-h-screen bg-[#121212] p-3 flex items-start justify-center" data-testid="fructan-widget-root">
      <Helmet>
        <title>{`Fruktan-Warnung ${data.region_label} · Bodenblick`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="w-full max-w-md border border-white/10 rounded-sm overflow-hidden font-[IBM_Plex_Sans,sans-serif]">
        {/* Header */}
        <div className="px-4 pt-4 pb-3 border-b border-white/10 bg-[#0A0A0A]">
          <div className="flex items-center gap-2">
            <Horse size={18} className="text-[#00FA9A]" />
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">
              Fruktan-Warnung für Pferde
            </div>
          </div>
          <h1 className="text-lg font-medium tracking-tight mt-0.5">{data.region_label}</h1>
          <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
            {data.country} · {data.bundesland}
          </div>
        </div>

        {/* Main status */}
        <div className="p-4" style={{ background: meta.bg }}>
          <div className="flex items-start gap-3">
            <span className="shrink-0 p-2 rounded-sm bg-black/60" style={{ color: meta.color }}>
              <Icon size={22} weight="fill" />
            </span>
            <div className="flex-1">
              <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>
                Stufe {score}/3 · {meta.label}
              </div>
              <div className="text-base font-medium mt-0.5" style={{ color: meta.color }}>
                {meta.headline}
              </div>
              <p className="text-xs text-white mt-1.5 leading-snug">{meta.body}</p>
            </div>
          </div>

          {/* Reasons */}
          {data.fructan_risk?.reasons?.length > 0 && (
            <ul className="mt-3 pl-9 space-y-1 text-[11px] text-zinc-400" data-testid="fructan-widget-reasons">
              {data.fructan_risk.reasons.map((r, i) => (
                <li key={i}>— {r}</li>
              ))}
            </ul>
          )}
        </div>

        {/* Nutrition row */}
        <div className="grid grid-cols-3 text-center border-t border-white/10">
          <Cell label="Protein" value={data.nutrition?.crude_protein_pct} unit="% TM" />
          <Cell label="Energie" value={data.nutrition?.nel_mj_per_kg_dm} unit="MJ/kg" />
          <Cell label="Aufwuchs" value={data.nutrition?.biomass_index} unit="/100" />
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-white/10 flex items-center justify-between text-[10px] font-mono text-zinc-500">
          <span>Stand: {updated}</span>
          <a
            href="https://bodenblick.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#00FA9A] underline"
            data-testid="fructan-widget-source-link"
          >
            bodenblick.com
          </a>
        </div>

        {/* Legal */}
        <div className="px-4 py-2 bg-[#0A0A0A] text-[9px] leading-snug text-zinc-500">
          {data.disclaimer} Keine tierärztliche Beratung.
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, unit }) {
  return (
    <div className="py-2.5">
      <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm font-mono font-medium">
        {value ?? "–"}
        <span className="text-[9px] text-zinc-500 ml-1">{unit}</span>
      </div>
    </div>
  );
}
